// T78 — event names for the lead-magnet funnel.
//
// A separate module rather than a constant inside `client.ts` for one structural reason:
// the worker half of this funnel (`api.leads.tsx`, `leads.server.ts`) needs the same
// names, and `client.ts` imports `posthog-js`, which must never be pulled into a
// server-only file. This module is pure data, so both halves can share it.
//
// Every name here MUST have a matching entry in
// `_docs/01-core/analytics-event-taxonomy.md` (R-55-002 taxonomy-drift rule).
// The taxonomy is append-only: to retire an event, mark it deprecated there — never
// rename it, because a rename silently orphans every historical row in PostHog.

export const ANALYTICS_EVENTS = {
  /** She taps the intro CTA — the commitment moment. */
  autoTestStarted: "auto_test_started",
  /** One per genuine answer selection. Carries the chosen value, not just the index. */
  autoTestQuestionAnswered: "auto_test_question_answered",
  /** Back-navigation inside the exit-free funnel. */
  autoTestStepBack: "auto_test_step_back",
  /** Best-effort: tab closed / hidden mid-funnel. */
  autoTestAbandoned: "auto_test_abandoned",
  /** Capture submitted. Server-side — this one is worker truth. */
  autoTestCompleted: "auto_test_completed",
  /** The result step rendered, either from the flow or from an `?r=` deep link. */
  autoTestResultViewed: "auto_test_result_viewed",
  /** The result CTA to the waitlist — the conversion moment of the whole lead magnet. */
  waitlistCtaClicked: "waitlist_cta_clicked",
  /** Waitlist capture accepted. Server-side. */
  waitlistJoined: "waitlist_joined",
  /** A `guias` capture was accepted (not a resend), with or without a PDF. Server-side. */
  guideRequest: "guide_request",
  /** A `guias` capture whose PDF was actually signed and handed back. Server-side. */
  guideDownloaded: "guide_downloaded",
  /**
   * Her persisted eligibility moved to subscribed through the optional guide checkbox — the
   * transition, decided by the write, not the checkbox. Server-side, best-effort.
   */
  newsletterSubscribed: "newsletter_subscribed",
  /** She clicked the auto-test bonus guide on the result step. */
  pdfBonusDownloaded: "pdf_bonus_downloaded",
  /** The transactional result email left for Resend. Server-side. */
  autotestResultEmailSent: "autotest_result_email_sent",
  /** T976 — she chose one of the home mirror's three sentences (`row`: which one). */
  homeMirrorClicked: "home_mirror_click",
} as const;

// T523 — the Rituales lifecycle moments (T57 retention model §1.A). First emission
// sites in the codebase: the assessment pass path (phase events) and the
// gamification queue consumer (activation + rhythm). Same taxonomy rule as above.
export const LIFECYCLE_EVENTS = {
  /** The high-water mark rose — she advanced to a phase she had never held. */
  phaseTransition: "phase_transition",
  /** Fired alongside phase_transition with `source: "progression"`. */
  phaseUnlocked: "phase_unlocked",
  /** The earned phase is the final one — maintenance begins. */
  maintenanceEntered: "maintenance_entered",
  /** Her first ever completed ritual session (any modality — Exprés counts). */
  firstRitualCompleted: "first_ritual_completed",
  /** 4 effective days inside any 7 consecutive calendar days (retention-model §1.A). */
  rhythmFormed: "rhythm_formed",

  // T542 — the two moments that come BEFORE the five above. Until this ticket the
  // first thing the product recorded about a new member was `firstRitualCompleted`,
  // i.e. after she had finished an entire training session; everything before it —
  // accepting the invitation, setting a password, meeting the consent ask, landing
  // on her phase — was dark. That is what would have made the legacy migration wave
  // invisible on the day it arrived.
  /**
   * Her FIRST established session — not the creation of her `users` row.
   *
   * The distinction is the whole point. Legacy members' rows were written months
   * earlier by the data migration (T193/T214) and they arrive with no password, so
   * their first access is the reset-password flow. Keyed on row creation this event
   * would have fired for the entire cohort in July and for nobody during the wave.
   */
  accountCreated: "account_created",
  /** She reached the end of onboarding and is being handed her dashboard. */
  onboardingCompleted: "onboarding_completed",
  /**
   * T864 — she closed the review of her answers she asked for at a programme reset
   * (submitted or skipped). Not a completion: `onboarding_completed` stays first-time only.
   */
  onboardingReviewed: "onboarding_reviewed",

  // T817 — every saved training, not only the first. `first_ritual_completed` answers
  // "did she ever train"; nothing answered "how many did she train, and on which days",
  // which is what return and frequency are made of. Emitted once per `session_progress`
  // row, after the row is written — never on opening the player or finishing a video.
  /** A training she finished was persisted (one `session_progress` row). */
  ritualCompleted: "ritual_completed",
  /** The player served her a session (opened, not completed). */
  ritualStarted: "ritual_started",
  // T913 — the save path, per execution, so support can tell a save she never sent from one the
  // server never received. `ritual_completed` stays the only event that says a row was written.
  /** A save request reached the player action (before the DAL ran). */
  ritualSaveAttempted: "ritual_save_attempted",
  /** What the DAL answered that request: stored, replayed, rejected or an error. */
  ritualSaveOutcome: "ritual_save_outcome",

  // T1000 slice 8 — Cápsula Cero, the free entry (taxonomy rows of T39, emitted server-side since
  // T1000: the claim and the last completed session are server facts, like `ritual_completed`).
  /** She claimed her free seat on Cápsula Cero (the `user_purchases` row was written). */
  capsulaCeroStarted: "capsula_cero_started",
  /** Every scheduled session of Cápsula Cero has a completed `session_progress` row; her 72 h window opened. */
  capsulaCeroCompleted: "capsula_cero_completed",
} as const;

// T913 — browser-side moments of the player, consent-gated like every client event: her tap on
// «He terminado» and an exit before it. Absent without analytics consent — absence proves nothing.
export const PLAYER_EVENTS = {
  finishDeclared: "player_finish_declared",
  exitUnfinished: "player_exit_unfinished",
} as const;

// T516 — the two offline-queue moments `pwa-contract.md` §7 names by name. Both were
// specified when the queue shipped and neither had an emitter, for the same reason the
// queue had no producer: nothing ever called `enqueue()`. Same taxonomy rule as above.
export const PWA_EVENTS = {
  /** A write she made with no network was persisted to the device instead of lost. */
  offlineQueued: "pwa_offline_queued",
  /** A queued write reached the server on reconnect. */
  offlineSynced: "pwa_offline_synced",
} as const;

/**
 * T523 — the `ritual_slug` the taxonomy's lifecycle events carry, derived from the
 * catalog id (`prod_ritual_sculpt` → `ritual-sculpt`). Products have no slug column;
 * the catalog id IS the stable identifier, so the mapping is mechanical rather
 * than a second source of truth.
 */
export function ritualSlugFromProductId(productId: string): string {
  return productId.replace(/^prod_/, "").replace(/_/g, "-");
}

/**
 * T78 — the commercial market a visitor belongs to.
 *
 * Exists because the product runs a SINGLE price across very different purchasing
 * powers. Un-segmented rates are therefore not just less useful, they are misleading:
 * a completion rate that drops from 60% to 50% tells you nothing about the funnel if
 * what actually changed is that a large LATAM cohort arrived that week. Every rate on
 * the dashboard has to be readable per market or it cannot be acted on.
 *
 * Four buckets, not a country code, because that is the grain decisions are made at —
 * and a raw country would also make each event more identifying than it needs to be.
 */
export type Market = 'es' | 'anglo' | 'latam' | 'other';

// Founder definition (2026-08-03): es = Spain · anglo = English-speaking · latam =
// Spanish-speaking countries OTHER than Spain · other = everything else. The split
// between `es` and `latam` is explicitly about purchasing power, not language.

const ES_MARKET = new Set(['ES']);

/**
 * The anglophone markets, scoped to the ones where a single price point is comparable
 * to Spain's.
 *
 * NOT every country with English as an official language: putting Nigeria, India or the
 * Philippines in the same bucket as the US would re-introduce exactly the purchasing-power
 * contamination this property exists to remove — the same mistake as folding Mexico into
 * `es`. Those fall to `other`, which is analysable rather than silently mixed in. Widen
 * this set if the founder wants a broader anglophone read.
 */
const ANGLO_MARKET = new Set(['US', 'GB', 'IE', 'CA', 'AU', 'NZ']);

/**
 * Spanish-speaking countries other than Spain.
 *
 * The bucket is named `latam` after the marketing vocabulary, but the RULE is
 * "hispanohablante ≠ España" — which is why Equatorial Guinea is here despite not being
 * Latin America. The rule is what the price sensitivity follows; the name is shorthand.
 */
const LATAM_MARKET = new Set([
  'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU', 'BO',
  'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR', 'GQ',
]);

/**
 * Bucket an ISO 3166-1 alpha-2 country code.
 *
 * `other` also absorbs the unknown case (no country header, Cloudflare's `XX`/`T1`),
 * deliberately: an explicit "not one of the three" is analysable, whereas a missing
 * property silently drops the event out of every market-filtered chart.
 */
export function marketFromCountry(country: string | null | undefined): Market {
  if (!country) return 'other';
  const cc = country.toUpperCase();
  if (ES_MARKET.has(cc)) return 'es';
  if (ANGLO_MARKET.has(cc)) return 'anglo';
  if (LATAM_MARKET.has(cc)) return 'latam';
  return 'other';
}

/** The markets the arc-3 KPI counts as buying (founder decision 2026-08-03). */
export const BUYING_MARKETS: readonly Market[] = ['es', 'anglo'];

/**
 * T817 — what kind of account produced a member event, so internal activity and
 * synthetic QA can be excluded from a real number without deleting anything.
 *
 * - `fixture` — a seeded/QA account: the `usr_e2e_` / `usr_dev_` / `usr_seed_` id prefix
 *   (`isFixtureUserId`, the T371 rule) or the hand-set `is_test_account` flag. Both are
 *   "not a real member" statements already used by every measured aggregate in D1.
 * - `internal` — an account on the `ADMIN_EMAILS` allowlist: the founder's own use.
 * - `member` — everyone else, testers included. A real tester IS a member; her cohort is
 *   selected in PostHog, not excluded here (D1 has no tester flag to read).
 *
 * Precedence is fixture > internal > member: a flag that says "this is not a real
 * account" is the stronger statement.
 */
export type AccountKind = "fixture" | "internal" | "member";

export function accountKindOf(args: {
  isFixture: boolean;
  isAdmin: boolean;
}): AccountKind {
  if (args.isFixture) return "fixture";
  if (args.isAdmin) return "internal";
  return "member";
}

/**
 * T78 — which deployment produced the event.
 *
 * PostHog's free plan allows exactly ONE project, so preview and production necessarily
 * share it. This property is what keeps them separable: without it, the founder's own QA
 * on a branch preview lands in the same funnel as real leads — and since those events only
 * fire after she accepts analytics, she is the single most likely person to generate them.
 * Low volume, but it distorts precisely when the totals are small and first being calibrated.
 *
 * Derived from the existing `ENVIRONMENT` var (`wrangler.toml`), so there is no new secret
 * and no second source of truth.
 *
 * T817 — an unset or unrecognised value reads `unknown`, no longer `local`. `local` is now
 * a claim only the local dev config makes (`ENVIRONMENT = "local"` in the root
 * `wrangler.toml`). The old fold is how the production background worker, which never
 * declared `ENVIRONMENT`, labelled four real production completions `local` in
 * September 2026 (verified against production D1 — see the T817 spec). Still never
 * `production` by default: a missing var must not inflate a real number, and `unknown`
 * is visible in a breakdown where a wrong `local` was not.
 */
export type AnalyticsEnvironment = 'local' | 'preview' | 'production' | 'unknown';

export function analyticsEnvironment(value: string | null | undefined): AnalyticsEnvironment {
  return value === 'production' || value === 'preview' || value === 'local' ? value : 'unknown';
}

/** Property bags accepted by both capture wrappers. PII (email, phone, name) is forbidden. */
export type EventProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * `is_legacy` — whether this event belongs to a woman migrating from Zenler rather than
 * to a new lead.
 *
 * **The history matters, because the answer moved twice.** ~~T78~~ shipped the property on
 * every funnel event with a module-private `isLegacyVisitor()` hardcoded to `false`,
 * honestly: nothing could tell the two apart, the only origin tag ever written was
 * `origin:native` (T320, `upsertLead`), and there were no legacy visitors on these routes
 * yet. Emitting it anyway meant the insights built then survived without a rebuild.
 *
 * T386 was named there as the ticket that would make it true, and it did — on 2026-09-17,
 * by turning the seam into the parameter below rather than into a cleverer body. No
 * function living in this module can answer the question: the flag depends on who the
 * visitor is, the answer is a row in D1, and this module is shared with the browser
 * precisely because it touches nothing.
 *
 * The URL-marker design T78 anticipated is dead. It assumed the invitation would carry a
 * tagged link; the wave went out as a message in a WhatsApp group with a plain address
 * typed into it, so there is no marker and there never will be for this cohort. Identity
 * is the signal that survives — `isLegacyMember` in `app/services/legacy-cohort.server.ts`
 * asks whether she holds a Zenler payment row, which still answers for a woman arriving
 * six months from now having lost the message.
 */

/**
 * Stamp the cross-cutting segmentation properties onto an event payload.
 *
 * One helper rather than three hand-written copies, for the reason the properties exist
 * at all: a segmentation property present on some events and absent on others produces
 * charts that quietly disagree with each other. Three call sites open-coding the same
 * object is precisely how that drift starts — the third one is always the one that
 * forgets. Used by the `/api/leads` action, `AnalyticsProvider.capture`, and the result
 * email in `leads.server.ts`.
 *
 * `environment` is the load-bearing one for data hygiene: with a single PostHog project
 * shared by preview and production, an event that cannot say where it came from cannot be
 * excluded from a real number.
 */
export function withSegmentation(
  properties: EventProperties,
  market: Market,
  environment: AnalyticsEnvironment,
  /**
   * See the `is_legacy` block above for where the answer comes from.
   *
   * **Defaults to `false`, and the default is a claim rather than a shrug.** Every caller
   * that cannot know the answer is one where the answer is false by construction: the
   * lead-magnet routes (`api.leads`, `leads.server`, `descarga`) capture people with no
   * account at all, and the browser provider has no database. A legacy member is by
   * definition someone who already bought, so on an anonymous capture `false` is the truth.
   *
   * It can be true where a session exists, and `account_created` is the one that matters:
   * it fires once per member ever (~~T542~~), so on 2026-09-17 it fired nineteen times for
   * women who had bought years earlier. Those nineteen were the whole problem.
   */
  isLegacy: boolean = false,
): EventProperties {
  return { ...properties, market, environment, is_legacy: isLegacy };
}

/**
 * The index reported for the `intro` step, so `from_index` / `to_index` stay numeric and
 * comparable in a PostHog query instead of mixing strings and numbers in one property.
 * Questions are 0-based; `intro` sits before question 0, and `capture` is reported as
 * `AUTOTEST_TOTAL_QUESTIONS` (immediately after the last question) by the surface, which
 * is the module that owns that constant.
 */
export const AUTOTEST_INTRO_INDEX = -1;
